import { TheBoatDBConfigType, useConfig } from '@marine/configs';
import { Image as AntImage, Button } from 'antd';
import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { SanityImage } from 'sanity-image';
import BoatImagePlaceholder from '../../../../packages/sailor-assets/svg/no-image-placeholder.svg';
import { urlForImage } from '../../sanity/lib/utils';
import styles from './BoatGallery.module.scss';
import { BoatGalleryProps } from './BoatGallery.props';

const BoatGallery: FC<BoatGalleryProps> = ({ files, image, boatName }) => {
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const { projectId, dataset } = useConfig<TheBoatDBConfigType>();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: 'snap',
    slides: {
      perView: 'auto',
      spacing: 10,
    },
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  const hasCoverImage = useMemo(() => !!image?.asset?._ref, [image?.asset?._ref]);

  const coverImageRef = useMemo(() => image?.asset?._ref, [image?.asset?._ref]);
  const coverPreviewUrl = useMemo(() => urlForImage(image)?.url(), [image]);

  const galleryItems = useMemo(
    () =>
      files?.map((file) => ({
        key: file._key,
        ref: file.asset?._ref,
        previewUrl: urlForImage(file)?.url(),
        alt: file.alt,
      })) ?? [],
    [files],
  );

  const previewImages = useMemo(() => {
    const images: { key: string; url: string | undefined; alt: string | undefined }[] = [];

    if (hasCoverImage) {
      images.push({ key: 'cover', url: coverPreviewUrl, alt: image?.alt });
    }

    for (const item of galleryItems) {
      images.push({ key: item.key, url: item.previewUrl, alt: item.alt });
    }

    return images;
  }, [coverPreviewUrl, galleryItems, hasCoverImage, image?.alt]);

  const handleOnItemImageClick = useCallback(
    (itemIndex: number) => {
      setPreviewIndex(hasCoverImage ? itemIndex + 1 : itemIndex);
      setShowPreview(true);
    },
    [hasCoverImage],
  );

  const handleCoverImageClick = useCallback(() => {
    setPreviewIndex(0);
    setShowPreview(true);
  }, []);

  const handlePreviewChange = useCallback((current: number) => {
    setPreviewIndex(current);
  }, []);

  return (
    <div className={styles.boatGalleryWrapper}>
      {coverImageRef ? (
        <div
          role="button"
          tabIndex={0}
          onClick={handleCoverImageClick}
          onKeyUp={(e) => e.key === 'Enter' && handleCoverImageClick()}
          className={styles.coverWrapper}
        >
          <SanityImage
            id={coverImageRef}
            alt={image?.alt ?? boatName ?? ''}
            projectId={projectId}
            dataset={dataset}
            width={1100}
            height={700}
          />
        </div>
      ) : (
        <div className={styles.noCoverWrapper}>
          <BoatImagePlaceholder />
        </div>
      )}
      <div className={styles.galleryWrapper}>
        {galleryItems.length ? (
          <>
            {loaded && !!instanceRef.current && !showPreview && (
              <ul className={styles.actionsNav}>
                <li>
                  <Button
                    type="text"
                    className={styles.itemNavArrow}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (showPreview) {
                        setPreviewIndex((previousIndex) => (previousIndex > 0 ? previousIndex - 1 : previousIndex));
                      } else {
                        instanceRef.current?.prev();
                      }
                    }}
                    icon={<ChevronLeft className={styles.itemNavArrowIcon} />}
                  />
                </li>
                <li>
                  <Button
                    type="text"
                    className={styles.itemNavArrow}
                    icon={<ChevronRight className={styles.itemNavArrowIcon} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (showPreview) {
                        setPreviewIndex((previousIndex) =>
                          previousIndex < previewImages.length - 1 ? previousIndex + 1 : previousIndex,
                        );
                      } else {
                        instanceRef.current?.next();
                      }
                    }}
                  />
                </li>
              </ul>
            )}
            <div ref={ref} className="keen-slider">
              {galleryItems.map((item, index) => (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOnItemImageClick(index)}
                  onKeyUp={(e) => e.key === 'Enter' && handleOnItemImageClick(index)}
                  key={item.key}
                  className={cn(styles.galleryImage, 'keen-slider__slide')}
                >
                  {item.ref && (
                    <SanityImage
                      id={item.ref}
                      alt={item.alt ?? boatName ?? ''}
                      projectId={projectId}
                      dataset={dataset}
                      width={170}
                      height={120}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <p>No gallery images</p>
        )}
        <div style={{ display: 'none' }}>
          <AntImage.PreviewGroup
            preview={{
              open: showPreview,
              onOpenChange: (isVisible) => setShowPreview(isVisible),
              current: previewIndex,
              onChange: handlePreviewChange,
            }}
          >
            {previewImages.map((item) => (
              <AntImage src={item.url} key={item.key} loading="lazy" alt={item.alt ?? boatName} />
            ))}
          </AntImage.PreviewGroup>
        </div>
      </div>
    </div>
  );
};

export { BoatGallery };
